import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiPaperAirplane, HiMicrophone } from 'react-icons/hi';
import { chatAPI } from '../../services/api';

// Voice input: record audio in the browser, transcribe on the backend (Groq
// Whisper). Works in every browser incl. Brave/Firefox, best-quality + free.
const canRecord =
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia;

const MAX_RECORDING_MS = 60000; // ponytail: safety cap so uploads stay small

// Playful "waking up" messages shown while the first session request is in flight
// (serverless cold start). Cycled, not artificially delayed — a warm start barely shows them.
const WAKING_MESSAGES = [
  'Lovish is waking up ☕',
  'Stretching… one sec',
  'Booting up the brain 🧠',
];

const ChatInput = ({
  inputText,
  inputRef,
  onInputChange,
  onKeyPress,
  onSendMessage,
  onSuggestionClick,
  canSendMessage,
  sessionId,
  userToken,
  rateLimits,
  isTyping,
  characterInfo,
  messages,
  isInitializing
}) => {
  const suggestedQuestions = [
    "What technologies do you work with?",
    "Tell me about your experience",
    "How can I contact you?"
  ];

  const [wakingIdx, setWakingIdx] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [micError, setMicError] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const stopTimerRef = useRef(null);
  const baseTextRef = useRef(''); // text already in the box when recording started

  const micDisabled =
    isTyping || isTranscribing || !sessionId || !userToken || rateLimits.dailyRemaining <= 0;

  const stopRecording = () => {
    clearTimeout(stopTimerRef.current);
    mediaRecorderRef.current?.stop(); // fires onstop, which does the upload
  };

  const startRecording = async () => {
    setMicError('');
    baseTextRef.current = inputText ? inputText + ' ' : '';

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error('Mic permission error:', err);
      setMicError('Microphone blocked. Allow mic access for this site and try again.');
      return;
    }

    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      setIsRecording(false);

      const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
      if (!blob.size) return;

      setIsTranscribing(true);
      try {
        const text = await chatAPI.transcribeAudio(blob);
        if (text) onInputChange({ target: { value: baseTextRef.current + text } });
        inputRef.current?.focus();
      } catch (err) {
        console.error('Transcription failed:', err);
        setMicError(err.message || 'Could not transcribe that. Please try again or type instead.');
      } finally {
        setIsTranscribing(false);
      }
    };

    mediaRecorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
    stopTimerRef.current = setTimeout(stopRecording, MAX_RECORDING_MS);
  };

  const toggleMic = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  // Cycle the "waking up" messages while the session is still connecting.
  useEffect(() => {
    if (sessionId) return;
    const id = setInterval(
      () => setWakingIdx((i) => (i + 1) % WAKING_MESSAGES.length),
      1400
    );
    return () => clearInterval(id);
  }, [sessionId]);

  // Stop recording if the component unmounts mid-capture.
  useEffect(() => () => {
    clearTimeout(stopTimerRef.current);
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
  }, []);

  return (
    <>
      {messages.length === 1 && !isInitializing && (
        <div className="app__ask-ai-suggestions">
          <h4>Try asking:</h4>
          <div className="suggestions-grid">
            {suggestedQuestions.map((question, index) => (
              <motion.button
                key={index}
                className="suggestion-button"
                onClick={() => onSuggestionClick(question)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {question}
              </motion.button>
            ))}
          </div>
        </div>
      )}

      <div className="app__ask-ai-input">
        {isRecording && (
          <div className="mic-listening-bar">
            <span className="mic-waves" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => <span key={i} className="mic-wave" />)}
            </span>
            <span>Recording… tap the mic to stop</span>
          </div>
        )}
        {isTranscribing && (
          <div className="mic-listening-bar">
            <span className="mic-spinner" aria-hidden="true" />
            <span>Transcribing…</span>
          </div>
        )}
        {micError && <div className="mic-error">{micError}</div>}
        <div className="input-container">
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={onInputChange}
            onKeyPress={onKeyPress}
            placeholder={!sessionId ? WAKING_MESSAGES[wakingIdx] : "Ask me anything or just say hi..."}
            rows="1"
            disabled={isTyping || !sessionId || !userToken || rateLimits.dailyRemaining <= 0}
            maxLength={characterInfo.maxCharacters}
          />
          {canRecord && (
            <button
              onClick={toggleMic}
              disabled={micDisabled}
              className={`mic-button ${isRecording ? 'listening' : ''}`}
              title={isRecording ? 'Stop recording' : 'Speak your message'}
              aria-label={isRecording ? 'Stop recording' : 'Speak your message'}
            >
              <HiMicrophone />
            </button>
          )}
          <button
            onClick={onSendMessage}
            disabled={!canSendMessage}
            className="send-button"
            title={
              !sessionId ? WAKING_MESSAGES[wakingIdx] :
              !userToken ? "Initializing..." :
              rateLimits.dailyRemaining <= 0 ? "Daily limit reached" :
              "Send message"
            }
          >
            <HiPaperAirplane />
          </button>
        </div>
        
        {/* Character count indicator */}
        {characterInfo.characterCount > 100 && (
          <div className={`character-count ${characterInfo.isNearLimit ? 'near-limit' : ''} ${characterInfo.isAtLimit ? 'at-limit' : ''}`}>
            {characterInfo.characterCount}/{characterInfo.maxCharacters}
          </div>
        )}
      </div>
    </>
  );
};

export default ChatInput; 